import { Player, DrmScheme, MediaType, PlaybackOptions } from '@quickplay/player-interface';

import { ExternalPlayer } from '../ExternalPlayer';
import { createCorePlayer } from './CorePlayer';
import { PlayerBuilder, SidecarTrack } from '../PlayerBuilder';
import { createLogger, Logger, LoggerLevel } from '@quickplay/foundation';
import { ThumbnailPreviewConfig } from '../thumbnailPreview/ThumbnailPreviewConfiguration';

const TAG_NAME = 'FL_PLAYER';

export function createCorePlayerBuilder(): PlayerBuilder {
  let contentUrl!: string;
  let mediaFormatType: MediaType = MediaType.DASH;
  let licenseUrl = '';
  let drmType: DrmScheme = DrmScheme.NONE;
  const options: PlaybackOptions = {
    autoPlayOnLoad: true,
    shouldRetryOnErrors: true,
    maxRetries: 1,
    retiresAttempted: 0,
    repeatWhenFinished: false,
    enforceHDCPCompliance: false,
    initialPlaybackTime: undefined,
    enableEngagementScore: false,
  };
  let videoElement: HTMLVideoElement;
  let videoWrapperElement: HTMLElement;
  let extPlayer: ExternalPlayer;
  let serverCertificate: ArrayBuffer;
  let sideloadedtracks: SidecarTrack[] | undefined;
  let defaultLogger: Logger = createLogger(LoggerLevel.OFF, TAG_NAME);
  let config: ThumbnailPreviewConfig;
  let thumbnailPreviewEndpointUrl: string | undefined;
  let webVTTThumbnailPreviewEndpointUrl: string | undefined;
  let library: any;
  let externalShakaPlayer_: any;

  return {
    get contentUrl(): string {
      return contentUrl;
    },
    get extPlayer(): any {
      return extPlayer || ExternalPlayer.ShakaPlayer;
    },

    get mediaFormatType(): MediaType {
      return mediaFormatType || MediaType.DASH;
    },

    get licenseUrl(): string {
      return licenseUrl || '';
    },

    get drmType(): DrmScheme {
      return drmType || DrmScheme.WIDEVINE;
    },

    get options(): PlaybackOptions {
      return options;
    },

    get configuredLogger(): Logger {
      return defaultLogger;
    },

    get videoElement(): HTMLVideoElement {
      return videoElement;
    },

    get videoWrapper(): HTMLElement {
      return videoWrapperElement;
    },

    get serverCertificate(): ArrayBuffer {
      return serverCertificate;
    },

    get getSideLoadedTracks(): SidecarTrack[] | undefined {
      return sideloadedtracks;
    },

    get config(): ThumbnailPreviewConfig {
      return config;
    },

    get thumbnailPreviewEndpointUrl(): string | undefined {
      return thumbnailPreviewEndpointUrl;
    },

    get webVTTThumbnailPreviewEndpointUrl(): string | undefined {
      return webVTTThumbnailPreviewEndpointUrl;
    },

    get library(): any {
      return library;
    },

    get externalShakaPlayer(): any {
      return externalShakaPlayer_;
    },

    mediaElement(element: HTMLVideoElement): PlayerBuilder {
      videoElement = element;
      return this;
    },

    mediaWrapperElement(element: HTMLElement): PlayerBuilder {
      videoWrapperElement = element;
      return this;
    },

    enableThumbnailPreview(thumbnailPreviewEndpointUrl_: string, config_: ThumbnailPreviewConfig = {}): PlayerBuilder {
      thumbnailPreviewEndpointUrl = thumbnailPreviewEndpointUrl_;
      config = config_;
      return this;
    },

    webVTTThumbnailPreview(webVTTTrackUrl: string): PlayerBuilder {
      webVTTThumbnailPreviewEndpointUrl = webVTTTrackUrl;
      return this;
    },

    mediaUrl(mediaUrl: string): PlayerBuilder {
      contentUrl = mediaUrl;
      return this;
    },

    mediaType(type: MediaType): PlayerBuilder {
      mediaFormatType = type;
      return this;
    },

    drmLicenseUrl(drmLicenseUrl: string): PlayerBuilder {
      licenseUrl = drmLicenseUrl;
      return this;
    },

    drmScheme(drmScheme: DrmScheme): PlayerBuilder {
      drmType = drmScheme;
      return this;
    },

    playbackOptions(playbackOptions: PlaybackOptions): PlayerBuilder {
      Object.assign(options, playbackOptions);
      return this;
    },

    usePlayer(externalPlayer: ExternalPlayer): PlayerBuilder {
      extPlayer = externalPlayer;
      return this;
    },

    certificate(certificate: ArrayBuffer): PlayerBuilder {
      serverCertificate = certificate;
      return this;
    },

    sideCarTracks(vttTracks: SidecarTrack[]): PlayerBuilder {
      sideloadedtracks = vttTracks;
      return this;
    },

    logger(logger: Logger): PlayerBuilder {
      defaultLogger = logger;
      return this;
    },

    playerLibrary(playerLib: any): PlayerBuilder {
      library = playerLib;
      return this;
    },

    shakaPlayer(player: any): PlayerBuilder {
      externalShakaPlayer_ = player;
      return this;
    },

    build(): Player {
      return createCorePlayer(this);
    },
  };
}
